import { useStore } from '@/infra/store';
import { useT } from '@/infra/i18n';
import { claim, listClaimable } from '@/services/afk-expedition';
import { COLORS, SPACING, RADIUS, FONT } from '@/presentation/theme/tokens';
import { overlayStyle, cardStyle } from '@/presentation/modals/modal-shared';
import { PixelIcon, type PixelIconName } from '@/presentation/icons/PixelIcon';

interface Props {
  open: boolean;
  onClose: () => void;
}

const ITEM_ICON: Record<string, PixelIconName> = {
  berry: 'berry',
  shell: 'shell',
  crystal: 'crystal',
  flower: 'flower',
};

export function AfkClaimModal({ open, onClose }: Props) {
  const t = useT();
  const npcs = useStore((s) => s.npcs);
  if (!open) return null;
  const claimable = listClaimable();
  if (claimable.length === 0) return null;

  const onClaim = (): void => {
    for (const d of claimable) {
      claim(d.id);
    }
    onClose();
  };

  return (
    <div role="dialog" data-testid="afk-claim-modal" style={overlayStyle} onClick={onClose}>
      <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
        <h2 style={titleStyle}>
          <PixelIcon name="inventory" size={28} color="#ffd6b3" />
          {t('afk.claim.title')}
        </h2>
        <p style={bodyStyle}>{t('afk.claim.body')}</p>
        <div style={listStyle}>
          {claimable.map((d) => {
            const npc = npcs.find((n) => n.id === d.npcId);
            return (
              <div key={d.id} style={rowStyle}>
                <span style={{ fontFamily: FONT.mono, color: COLORS.accent }}>
                  {npc?.name ?? '?'}
                </span>
                <span style={{ fontSize: FONT.sizeXs, color: COLORS.textDim }}>
                  {t(`biome.${d.biome}`)}
                </span>
                {d.rewards && (
                  <span style={rewardList}>
                    {Object.entries(d.rewards.items).map(([k, v]) => (
                      <span key={k} style={rewardItem}>
                        <PixelIcon name={ITEM_ICON[k] ?? 'inventory'} size={16} color="#ffd6b3" />
                        x{v}
                      </span>
                    ))}
                  </span>
                )}
              </div>
            );
          })}
        </div>
        <button data-testid="afk-claim-confirm" onClick={onClaim} style={confirmBtn}>
          {t('afk.claim.confirm')}
        </button>
      </div>
    </div>
  );
}

const titleStyle: React.CSSProperties = {
  margin: `0 0 ${SPACING.sm}px 0`,
  fontSize: FONT.sizeLg,
  color: COLORS.accent,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: SPACING.xs,
};
const bodyStyle: React.CSSProperties = {
  color: COLORS.textDim,
  fontSize: FONT.sizeSm,
  textAlign: 'center',
  marginBottom: SPACING.md,
};
const listStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: SPACING.xs,
  marginBottom: SPACING.lg,
  maxHeight: 300,
  overflowY: 'auto',
};
const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: SPACING.sm,
  padding: SPACING.sm,
  background: COLORS.surfaceAlt,
  borderRadius: RADIUS.sm,
  border: `1px solid ${COLORS.border}`,
};
const rewardList: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: FONT.sizeXs,
};
const rewardItem: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 2,
};
const confirmBtn: React.CSSProperties = {
  width: '100%',
  background: COLORS.accent,
  color: '#000',
  border: 'none',
  borderRadius: RADIUS.md,
  padding: '8px 14px',
  cursor: 'pointer',
  fontWeight: 600,
};
